import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarClock, Check, ShieldCheck, MonitorSmartphone, FileSearch, ClipboardCheck } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal, Kicker } from "../components/Reveal";
import { CALENDLY_URL, FAQS } from "../data/content";
import { Faq } from "../components/Faq";
import { ContactForm } from "../components/ContactForm";

const COVERAGE = [
  {
    icon: ShieldCheck,
    title: "FINRA Rule 3310 Independent Testing",
    body: "The objective, documented annual independent test your AML program requires — scoped to your business model, delivery channels, and customer base.",
  },
  {
    icon: MonitorSmartphone,
    title: "Online Platform Audits & Evaluations",
    body: "Specialized audits and evaluations for broker-dealers launching online platforms — onboarding flows, identity verification, funding mechanics, and communications review built for digital distribution.",
  },
  {
    icon: FileSearch,
    title: "Gap Analysis & Remediation",
    body: "Clear, prioritized findings with practical remediation guidance — policies, procedures, and controls that hold up under examination.",
  },
  {
    icon: ClipboardCheck,
    title: "New Broker-Dealer Readiness",
    body: "Readiness reviews for firms entering the online space: AML program design, supervisory procedures, and the documentation examiners expect from day one.",
  },
];

const PROCESS = [
  { step: "Scope", body: "We define the audit scope with you — business lines, platforms, customer types, and prior findings." },
  { step: "Fieldwork", body: "Testing of your program against Rule 3310 and FinCEN requirements: CIP, monitoring, SAR processes, and training." },
  { step: "Findings", body: "A written report with clear, prioritized findings — practical, documented, and examination-ready." },
  { step: "Remediate", body: "Hands-on support implementing fixes and strengthening the program before your next examination." },
];

const AmlAudits = () => (
  <div data-testid="aml-audits-page">
    <PageHero
      kicker="AML Independent Audits"
      lines={[<span key="a">Independent AML testing,</span>, <span key="b">built for the <em className="italic text-[#3E9BDC]">online era.</em></span>]}
      intro="American Elm provides AML independent audits for broker-dealers — delivering the objective annual testing FINRA expects, with a specialty in audits and evaluations for BDs launching online platforms. We have guided new broker-dealers entering this fast-growing space from initial structuring through their first offerings."
      meta={["FINRA Rule 3310", "Broker-Dealers", "Online Platforms", "New BD Launches"]}
    />

    <div className="bg-[#0A2B3D]" data-testid="aml-hero-cta">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12 pb-16 md:pb-20">
        <a href="#consultation" data-testid="aml-hero-cta-button" className="btn-gold">
          Request a Consultation <ArrowRight size={16} />
        </a>
      </div>
    </div>

    <section className="py-20 md:py-28" data-testid="aml-who">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12">
        <Reveal>
          <Kicker>Who We Serve</Kicker>
          <h2 className="mt-5 font-serif font-bold text-3xl sm:text-4xl text-[#0A2B3D] tracking-tight max-w-3xl leading-[1.15]">
            Traditional brokerages and technology-forward platforms alike.
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { title: "Established Broker-Dealers", body: "Firms that need their annual independent AML test performed by experienced, objective auditors — with findings that drive real improvement, not boilerplate." },
            { title: "BDs Launching Online Platforms", body: "Firms building digital onboarding, online offering distribution, or platform-based capital raising — where customer journeys and funding mechanics make AML look different." },
            { title: "New Broker-Dealers", body: "Entrants to this fast-growing space who need an AML program designed correctly from the start, plus readiness reviews before the first examination cycle." },
          ].map((c, i) => (
            <Reveal key={c.title} delay={i * 0.07}>
              <div className="h-full p-8 md:p-10 bg-[#EBF3F8] border border-[#A9CFE2]/50" data-testid={`aml-audience-${i}`}>
                <h3 className="font-serif font-bold text-xl text-[#0A2B3D]">{c.title}</h3>
                <p className="mt-3 text-sm text-[#475569] leading-relaxed">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 md:py-28 border-t border-[#0A2B3D]/10" data-testid="aml-coverage">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12">
        <Reveal>
          <Kicker>Audit Coverage</Kicker>
          <h2 className="mt-5 font-serif font-bold text-3xl sm:text-4xl text-[#0A2B3D] tracking-tight max-w-2xl leading-[1.15]">
            What an American Elm audit covers.
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {COVERAGE.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.06}>
              <div className="h-full p-8 md:p-10 bg-white border border-[#0A2B3D]/12 hover:border-[#3E9BDC]/60 transition-colors duration-500" data-testid={`aml-coverage-${i}`}>
                <c.icon size={24} className="text-[#3E9BDC]" />
                <h3 className="mt-4 font-serif font-bold text-xl text-[#0A2B3D] leading-snug">{c.title}</h3>
                <p className="mt-3 text-sm text-[#475569] leading-relaxed">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15} className="mt-10">
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3" data-testid="aml-detail-list">
            {[
              "Customer identification program (CIP) testing",
              "Onboarding and identity verification flow review",
              "Transaction and activity monitoring evaluation",
              "Suspicious activity escalation and SAR processes",
              "AML training program assessment",
              "Written supervisory procedures for online distribution",
            ].map((item) => (
              <li key={item} className="flex gap-3 text-sm text-[#475569]">
                <Check size={15} className="text-[#3E9BDC] mt-0.5 shrink-0" /> {item}
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>

    <section className="bg-[#0A2B3D] text-white py-20 md:py-28" data-testid="aml-process">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12">
        <Reveal>
          <Kicker light>The Engagement</Kicker>
          <h2 className="mt-5 font-serif font-bold text-3xl sm:text-4xl tracking-tight max-w-2xl leading-[1.15]">
            Scope. Fieldwork. Findings. Remediate.
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PROCESS.map((p, i) => (
            <Reveal key={p.step} delay={i * 0.08}>
              <div className="h-full border border-white/15 p-8 bg-[#082838] hover:border-[#3E9BDC]/60 transition-colors duration-500" data-testid={`aml-step-${i}`}>
                <h3 className="font-serif font-bold text-2xl">{p.step}</h3>
                <p className="mt-3 text-sm text-white/65 leading-relaxed">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2} className="mt-12">
          <p className="text-white/60 text-sm max-w-3xl leading-relaxed" data-testid="aml-outcome-note">
            The outcome: an independent audit that satisfies Rule 3310, findings you can act on, and
            a program that stands up when FINRA comes calling — whether your clients walk in or log in.
          </p>
        </Reveal>
      </div>
    </section>

    <Faq items={FAQS.aml} id="aml-faq" />

    <div className="max-w-4xl mx-auto px-5 sm:px-8 -mt-6 md:-mt-10 pb-4" data-testid="aml-faq-explore-more">
      <Link to="/aml-independent-audits-faq" data-testid="aml-explore-more-link" className="inline-flex items-center gap-2 text-sm font-semibold text-[#3E9BDC] hover:text-[#0A2B3D] transition-colors underline underline-offset-4">
        Explore more AML audit questions <ArrowRight size={15} />
      </Link>
    </div>

    <section id="consultation" className="scroll-mt-24 bg-[#A9CFE2] py-16 md:py-24" data-testid="aml-consultation">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
        <div>
          <Kicker>Request a Consultation</Kicker>
          <h2 className="mt-5 font-serif font-bold text-3xl sm:text-4xl text-[#0A2B3D] tracking-tight">Schedule your independent audit.</h2>
          <p className="mt-4 text-[#0A2B3D]/75 max-w-xl leading-relaxed">Annual testing cycle approaching — or a platform launch on the horizon? Tell us about your firm and we'll be in touch. No obligation.</p>
          <a href={CALENDLY_URL} target="_blank" rel="noreferrer" data-testid="aml-calendly-link" className="inline-flex items-center gap-2 mt-6 text-sm font-semibold text-[#0A2B3D] hover:text-[#082838] underline underline-offset-4">
            <CalendarClock size={16} /> Or schedule a 15-minute call
          </a>
        </div>
        <ContactForm compact withPhone defaultTopic="AML Independent Audit" defaultServices={["AML Independent Audit"]} />
      </div>
    </section>
  </div>
);

export default AmlAudits;
